import React, { useState, useEffect, useCallback } from "react";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { format, isValid } from "date-fns";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Loader2, ArrowLeft } from "lucide-react";
// @ts-ignore
import { useUserContext } from "../../../Contexts/UserProvider.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { useTenantManagement } from "../../Services/TenantManagement";
import { toast } from "react-toastify";
import { useFileUploader } from "../../../BlobStorage/BlobServiceClient";
import { inputFieldValidator } from "../../../Utilities/validation";
import { Checkbox } from "../../../components/ui/checkbox";
import { useDropdownData } from "../../Services/Common";
import { isApiError, getApiErrorMessage } from "../../../Utilities/apiResponse";

const parseSafeDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date && isValid(value)) return value;
  const parsed = new Date(value as string);
  return isValid(parsed) ? parsed : null;
};

const toDateInputValue = (value: unknown): string => {
  const date = parseSafeDate(value);
  if (!date) return "";
  return format(date, "yyyy-MM-dd");
};

const DetailForm = () => {
  const { companyTypes, industries, countries } = useDropdownData();
  const { updateTenant, getTenantDetail } = useTenantManagement();
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { uploadFile } = useFileUploader();
  const { tenant, replaceTenant } = useUserContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const [detailForm, setDetailForm] = useState({
    id: null,
    name: null,
    code: null,
    legalName: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    industryId: null,
    companyTypeId: null,
    businessRegNumber: "",
    countryId: null,
    email: "",
    telephone: "",
    website: "",
    currency: "",
    timeZone: "",
    logo: "",
    fiscalYearStart: new Date(),
    fiscalYearEnd: new Date(),
    logoFile: null as File | null,
    defaultLanguage: "",
    billingContactName: "",
    billingContactEmail: "",
    billingContactPhone: "",
    description: undefined,
    kysStatus: 0,
    isDiscoverable: false
  });

  const [errors, setErrors] = useState({
    name: false,
    companyTypeId: false,
    industryId: false,
    countryId: false,
  });

  const syncCurrenciesForCountry = useCallback((countryId: string | null, currentCurrency?: string) => {
    if (!countryId || !countries?.length) {
      setCurrencies([]);
      return;
    }
    const country = countries.find((item) => item.id === countryId);
    const countryCurrencies: string[] = country?.currency || [];
    setCurrencies(countryCurrencies);

    if (countryCurrencies.length === 1) {
      setDetailForm((prev) => ({ ...prev, currency: countryCurrencies[0] }));
    } else if (currentCurrency && !countryCurrencies.includes(currentCurrency)) {
      setDetailForm((prev) => ({ ...prev, currency: "" }));
    }
  }, [countries]);

  const onChange = (event, type?) => {
    const detailType = type ? type : event?.target?.name;
    let detailValue = type ? event : event?.target?.value;

    if (detailType === "fiscalYearStart" || detailType === "fiscalYearEnd") {
      detailValue = parseSafeDate(detailValue) || detailValue;
    }

    if (detailType === "countryId") {
      syncCurrenciesForCountry(detailValue, detailForm.currency);
    }

    setDetailForm((prev) => ({
      ...prev,
      [detailType]: detailValue,
    }));

    const isEmptyValue =
      typeof detailValue === "boolean"
        ? false
        : detailValue instanceof Date
          ? false
          : typeof detailValue === "string"
            ? detailValue.trim() === ""
            : detailValue?.type
              ? detailValue?.name?.trim() === ""
              : detailValue == null || detailValue === "";

    setErrors((prev) => ({
      ...prev,
      [detailType]: isEmptyValue,
    }));
  };

  useEffect(() => {
    if (!tenant) return;
    setDetailForm((prev) => ({
      ...prev,
      ...tenant,
      fiscalYearStart: parseSafeDate(tenant.fiscalYearStart) || prev.fiscalYearStart,
      fiscalYearEnd: parseSafeDate(tenant.fiscalYearEnd) || prev.fiscalYearEnd,
    }));
    syncCurrenciesForCountry(tenant.countryId, tenant.currency);
  }, [tenant?.code, id, tenant, syncCurrenciesForCountry]);

  const handleSave = async () => {
    setLoading(true);
    const newErrors = {
      name: inputFieldValidator(detailForm?.name, "string"),
      companyTypeId: inputFieldValidator(detailForm?.companyTypeId, "string"),
      industryId: inputFieldValidator(detailForm?.industryId, "string"),
      countryId: inputFieldValidator(detailForm?.countryId, "string"),
    };

    setErrors(newErrors);

    const hasError = Object.values(newErrors).some((v) => v);
    if (hasError) return setLoading(false);

    const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, '');
    let logoDto: File | null = null;

    if (detailForm?.logoFile instanceof File) {
      logoDto = new File(
        [detailForm?.logoFile], // file content
        `${detailForm?.logoFile?.name.split('.')[0]}-${id}-${timestamp}.${detailForm?.logoFile?.name.split('.').pop()}`, // new name
        { type: detailForm?.logoFile?.type } // original MIME type
      );
    }

    let logoUrl = logoDto?.name;

    if (detailForm?.logoFile && logoDto) {
      const response = await uploadFile("company-logo", logoDto);
      logoUrl = response;
    }

    const startDate = parseSafeDate(detailForm.fiscalYearStart);
    const endDate = parseSafeDate(detailForm.fiscalYearEnd);

    if (startDate && endDate && endDate < startDate) {
      toast.error("Financial year end cannot be before start date");
      setLoading(false);
      return;
    }

    const finalDto = {
      id: detailForm.id,
      name: detailForm.name,
      code: detailForm.code,
      legalName: detailForm.legalName,
      address: detailForm.address,
      city: detailForm.city,
      state: detailForm.state,
      postalCode: detailForm.postalCode,
      industryId: detailForm.industryId,
      companyTypeId: detailForm.companyTypeId,
      businessRegNumber: detailForm.businessRegNumber,
      countryId: detailForm.countryId,
      email: detailForm.email,
      telephone: detailForm.telephone,
      website: detailForm.website,
      currency: detailForm.currency,
      timeZone: detailForm.timeZone,
      logo: logoUrl || tenant?.logo,
      fiscalYearStart: startDate ? startDate.toISOString() : detailForm.fiscalYearStart,
      fiscalYearEnd: endDate ? endDate.toISOString() : detailForm.fiscalYearEnd,
      defaultLanguage: detailForm.defaultLanguage,
      billingContactName: detailForm.billingContactName,
      billingContactEmail: detailForm.billingContactEmail,
      billingContactPhone: detailForm.billingContactPhone,
      description: detailForm.description,
      kysStatus: detailForm.kysStatus,
      isDiscoverable: detailForm.isDiscoverable
    };

    try {
      const response = await updateTenant(finalDto);
      if (isApiError(response)) {
        toast.error(getApiErrorMessage(response));
        setLoading(false);
        return;
      }

      toast.success("Details Updated Successfully");

      const tenantDetails = await getTenantDetail();
      if (isApiError(tenantDetails)) {
        const industryName = industries?.find((item) => item.id === finalDto.industryId)?.name || tenant?.industry;
        const companyTypeName = companyTypes?.find((item) => item.id === finalDto.companyTypeId)?.name || tenant?.companyType;
        replaceTenant({
          ...(tenant || {}),
          ...finalDto,
          industry: industryName,
          companyType: companyTypeName,
        } as typeof tenant);
      } else {
        replaceTenant(tenantDetails);
      }

      setLoading(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="container mx-auto px-4 py-6">
        <div className="w-full max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Button variant="outline" onClick={() => navigate(`/app/${id}/dashboard`)} className="mb-8">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold">Edit Company Profile</h1>
            </div>
          </div>
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="font-bold text-lg">
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="col-span-1 md:col-span-3">
                  <div className="border-2 border-dashed rounded-lg p-6 text-center">
                    <div className="mx-auto w-16 h-16 mb-4">
                      <img
                        src={detailForm?.logo ? detailForm?.logo : "/Assets/placeholder.svg"}
                        alt="Company logo placeholder"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <Input
                      type="file"
                      name="logoFile"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        console.log("Files Target : ", event.target.files);
                        console.log("Files : ", file);
                        onChange(file, "logoFile")
                      }}
                    >
                    </Input>
                    <p className="text-sm text-gray-500 mt-2">
                      Upload your company logo (PNG, JPG, max 2MB)
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold">Display Name <span className="text-red-500">*</span></label>
                  <Input
                    placeholder="Display name"
                    name="name"
                    className={errors.name ? "border-red-500 focus-visible:ring-red-500" : ""}
                    value={detailForm?.name}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">Legal Name</label>
                  <Input
                    placeholder="Legal name"
                    name="legalName"
                    value={detailForm?.legalName}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">Company Type <span className="text-red-500">*</span></label>
                  <Select
                    value={detailForm?.companyTypeId || undefined}
                    onValueChange={(event) => onChange(event, "companyTypeId")}
                  >
                    <SelectTrigger
                      className={errors.companyTypeId ? "border-red-500 focus-visible:ring-red-500" : ""}
                    >
                      <SelectValue placeholder="Select the Company Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {companyTypes.map((item, index) => (
                        <SelectItem key={index} value={item?.id}>
                          {item?.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold">Industry <span className="text-red-500">*</span></label>
                  <Select
                    value={detailForm?.industryId || undefined}
                    onValueChange={(event) => onChange(event, "industryId")}
                  >
                    <SelectTrigger
                      className={errors.industryId ? "border-red-500 focus-visible:ring-red-500" : ""}
                    >
                      <SelectValue placeholder="Select the Industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((item, index) => {
                        return (
                          <SelectItem key={index} value={item?.id}>
                            {item?.description}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold">
                    Registration Number
                  </label>
                  <Input
                    placeholder="Registration number"
                    name="businessRegNumber"
                    value={detailForm?.businessRegNumber}
                    onChange={(event) => onChange(event)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="font-bold text-lg">
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="col-span-1 md:col-span-1">
                  <label className="text-xs font-bold">Address</label>
                  <Input
                    placeholder="Address of your company"
                    name="address"
                    value={detailForm?.address}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">City</label>
                  <Input
                    placeholder="Company located city"
                    name="city"
                    value={detailForm?.city}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">State</label>
                  <Input
                    placeholder="State"
                    name="state"
                    value={detailForm?.state}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">Postal Code</label>
                  <Input
                    placeholder="Postal code"
                    name="postalCode"
                    value={detailForm?.postalCode}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">Country <span className="text-red-500">*</span></label>
                  <Select
                    value={
                      detailForm?.countryId ? detailForm?.countryId : undefined
                    }
                    onValueChange={(event) => {
                      onChange(event, "countryId");
                      console.log("Event : ", event);
                    }}
                  >
                    <SelectTrigger
                      className={errors.countryId ? "border-red-500 focus-visible:ring-red-500" : ""}
                    >
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries?.map((country) => (
                        <SelectItem key={country?.id} value={country?.id}>
                          {country?.countryName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold">Email</label>
                  <Input
                    placeholder="Email"
                    type="email"
                    name="email"
                    value={detailForm?.email}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">Telephone</label>
                  <Input
                    placeholder="Telephone"
                    type="tel"
                    name="telephone"
                    value={detailForm?.telephone}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">
                    Billing Contact Name
                  </label>
                  <Input
                    placeholder="Billing contact name"
                    name="billingContactName"
                    value={detailForm?.billingContactName}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">
                    Billing Contact Email
                  </label>
                  <Input
                    placeholder="Billing contact email"
                    type="email"
                    name="billingContactEmail"
                    value={detailForm?.billingContactEmail}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">
                    Billing Contact Phone
                  </label>
                  <Input
                    placeholder="Billing contact phone"
                    type="tel"
                    name="billingContactPhone"
                    value={detailForm?.billingContactPhone}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">Website</label>
                  <Input
                    placeholder="Website"
                    name="website"
                    value={detailForm?.website}
                    onChange={(event) => onChange(event)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">Description</label>
                  <Textarea
                    placeholder="Description"
                    name="description"
                    onChange={(event) => onChange(event)}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold mr-2 p-2">Is Discoverable</label>
                  <Checkbox
                    checked={detailForm?.isDiscoverable}
                    name="isDiscoverable"
                    onCheckedChange={(checked) => onChange(checked, "isDiscoverable")}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financial Information */}
          <Card>
            <CardHeader>
              <CardTitle className="font-bold text-lg">
                Financial Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-bold">
                    Financial Year Start
                  </label>
                  <Input
                    type="date"
                    name="fiscalYearStart"
                    value={toDateInputValue(detailForm?.fiscalYearStart)}
                    onChange={(event) => onChange(event)}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">
                    Financial Year End
                  </label>
                  <Input
                    type="date"
                    name="fiscalYearEnd"
                    value={toDateInputValue(detailForm?.fiscalYearEnd)}
                    onChange={(event) => onChange(event)}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold">Currency</label>
                  <Select
                    value={detailForm?.currency || undefined}
                    onValueChange={(event) => onChange(event, "currency")}
                    disabled={!detailForm?.countryId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select the currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies?.map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="d-flex justify-content-end align-item-end">
            <Button onClick={handleSave} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Update Changes"
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DetailForm;
